/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  Spinner,
  SvgIcon,
  DetailPageHeader,
  ToolbarIdentityCases,
  CreateCaseModal,
  IdentityPageNavigation,
  IdentityDetail,
  EmployerIdentityDetail,
  Warning
} from '../components'

import {
  isBase64,
  decodeBase64,
  readablePhoneNumber,
  getNodeTypeFromBase64Id,
  getContentTypeFromNodeType
} from '../utils'

import { CONTENTTYPE_CASE } from '../constants'

const createCaseModalRef = React.createRef()
const casesCountAndCreateRef = React.createRef()

const initialiseFromUrl = (props) => {
  if (props.location.pathname.length > props.match.url.length) {
    let lastPathSegment = props.location.pathname.split('/').pop()
    let detail = lastPathSegment.split('?')[0]  // drops query string part

    if (isBase64(detail)) {
      switch(getNodeTypeFromBase64Id(detail)) {
        case 'PayrollCycleNode':
          return { detail: 'payrollcycles', detailObjectId: detail }

        case 'LoanNode':
          return { detail: 'credit', detailObjectId: detail }

        case 'CreditNode':
          return { detail: 'credit', detailObjectId: detail }

        case 'ClientInvestmentNode':
          return { detail: 'investments', detailObjectId: detail }

        case 'ClientFuneralPolicyNode':
          return { detail: 'insurance', detailObjectId: detail }

        case 'CaseNode':
          return { detail: 'cases', detailObjectId: detail }

        case 'RequestRunNode':
          return { detail: 'requests', detailObjectId: detail }

        case 'AccountNode':
          return { detail: 'accounts', detailObjectId: detail }

        case 'WhatsappMessageNode':
          return { detail: 'whatsapp', detailObjectId: detail }

        case 'WithdrawalNode':
          return { detail: 'withdrawals', detailObjectId: detail }

        default:
          return { detail, detailObjectId: null }
      }
    } else {
      return { detail, detailObjectId: null }
    }
  } else {
    return { detail: 'summary', detailObjectId: null }
  }
}


class Identity extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string
  }

  static getDerivedStateFromProps(props, state) {
    return initialiseFromUrl(props)
  }

  state = {}

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (createCaseModalRef.current && !createCaseModalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showCreateCaseModal: false })
    }
  }

  getIdentityTypesArray = (identity) => {
    let identityTypes = []

    if (identity.isClient) {
      identityTypes.push('client')
    }
    if (identity.isEmployer) {
      identityTypes.push('employer')
    }
    if (identity.isContact) {
      identityTypes.push('contact')
    }

    return identityTypes
  }

  getCaseRelations = (objectId) => {
    const nodeType = getNodeTypeFromBase64Id(objectId)
    const contentType = getContentTypeFromNodeType(nodeType)

    if (contentType === CONTENTTYPE_CASE) {
      return null  // we're assuming that no created cases will need other cases as relations
    }

    return [{ objectId: decodeBase64(objectId), contentType }]
  }

  showCreateCaseModal = (identityId) => (
    <CreateCaseModal
      forwardedRef={createCaseModalRef}
      followUp={true}
      affects={identityId}
      caseRelations={this.state.detailObjectId
        ? this.getCaseRelations(this.state.detailObjectId)
        : null
      }
      onCloseClick={() => this.setState({ showCreateCaseModal: false })}
      onSaveClick={() => {
        this.setState({
          showCreateCaseModal: false,
          caseAdditionTimeStamp: moment().unix()  // moment().unix() gets Unix timestamp in seconds
        })
        casesCountAndCreateRef.current.props.updateData()  // updates CasesCountAndCreate open/resolved cases count
      }}
    />
  )

  getStatuses = (identity) => {
    if (identity.isClient) {
      return [
        {
          title: 'ID number',
          value: identity.identityValue
        },
        {
          title: 'Cellphone',
          value: identity.defaultCellphoneNumber.edges.length > 0
            ? readablePhoneNumber(identity.defaultCellphoneNumber.edges[0].node.addressValue)
            : '-'
        }
      ]
    } else if (identity.isEmployer) {
      let employerStatusInfo = [
        {
          title: 'Company name',
          value: identity.fullName
        },
        {
          title: 'CIPC number',
          value: identity.identityValue
        }
      ]

      if (identity.employer.employergroup) {
        employerStatusInfo.unshift({
          title: 'Group name',
          value: identity.employer.employergroup.friendlyName
        })
      }

      return employerStatusInfo
    } else if (identity.isContact) {
      let preferredAddressType = identity.defaultAddressType

      let hasDefaultActiveCellphoneNumber = identity.defaultCellphoneNumber.edges.length > 0   // or instead use identity.msisdnDefault
      let hasDefaultActiveEmailAddress = identity.defaultEmailAddress.edges.length > 0

      let defaultActiveCellphoneNumber = hasDefaultActiveCellphoneNumber
        ? readablePhoneNumber(identity.defaultCellphoneNumber.edges[0].node.addressValue)
        : '-'
      let defaultActiveEmailAddress = hasDefaultActiveEmailAddress
        ? identity.defaultEmailAddress.edges[0].node.addressValue
        : '-'

      let contactStatusInfo =  [
        {
          title: 'ID number',
          value: identity.identityValue
            ? identity.identityValue
            : '-'
        }
      ]

      if (preferredAddressType === 'MSISDN') {
        contactStatusInfo.push({
          title: hasDefaultActiveCellphoneNumber
            ? 'Cellphone'
            : hasDefaultActiveEmailAddress
              ? 'Email'
              : null,
          value: hasDefaultActiveCellphoneNumber  // or instead use identity.msisdnDefault
            ? readablePhoneNumber(identity.defaultCellphoneNumber.edges[0].node.addressValue)
            : defaultActiveEmailAddress
        })
      } else if (preferredAddressType === 'EMAIL') {
        contactStatusInfo.push({
          title: hasDefaultActiveEmailAddress
            ? 'Email'
            : hasDefaultActiveCellphoneNumber
              ? 'Cellphone'
              : null,
          value: defaultActiveEmailAddress
            ? identity.defaultEmailAddress.edges[0].node.addressValue
            : defaultActiveCellphoneNumber
        })
      }

      return contactStatusInfo
    } else {
      return []
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    let identity = this.props.data.identity
    let identityTypes = identity ? this.getIdentityTypesArray(identity) : null

    if (identity === null) {
      return <Warning className='slds-p-around_large'>No identity found!</Warning>
    }

    return (
      <div className='slds-grid slds-grid_vertical' style={{ height: '90vh', overflow: 'hidden' }}>
        <div>
          <DetailPageHeader
            title={identity.isEmployer
              ? identity.employer.friendlyName
              : identity.fullName
            }
            summaryLine1={identity.summary}
            summaryLine2={identity.isClient
              ? identity.client.summary
              : null
            }
            icon={
              <SvgIcon
                variant='utility'
                symbol={identity.isEmployer ? 'company' : 'user'}
                colour='light'
              />
            }
            iconText={identityTypes[0]}
            statuses={this.getStatuses(identity)}
            action={
              <ToolbarIdentityCases
                forwardedRef={casesCountAndCreateRef}
                identityId={identity.id}
                elementId='detailPageHeader'
                onCaseCreateClick={() => this.setState({ showCreateCaseModal: true })}
              />
            }
          />
          <IdentityPageNavigation
            identityId={identity.id}
            identityTypes={identityTypes}
            onTabClick={(tabName, objectId) => {
              // clear url search parameters if any
              if (this.props.location.search) {
                this.props.history.replace({ search: null })
              }
              this.setState({ detail: tabName, detailObjectId: objectId })
            }}
            initialTab={this.state.detailObjectId || this.state.detail}
          />
        </div>
        <div className='stage-main slds-scrollable_y' role='main'>
          {this.state.detail
            ? identity.isEmployer
              ? <EmployerIdentityDetail
                  identityId={identity.id}
                  detail={this.state.detail}
                  objectId={this.state.detailObjectId}
                  key={this.state.caseAdditionTimeStamp}
                  // whenever a case is added, IdentityDetail will be rerendered to ensure that the identity case listing is updated
                />
              : <IdentityDetail
                  identityId={identity.id}
                  detail={this.state.detail}
                  objectId={this.state.detailObjectId}
                  key={this.state.caseAdditionTimeStamp}
                  // whenever a case is added, IdentityDetail will be rerendered to ensure that the identity case listing is updated
                />
            : null
          }
        </div>
        {this.state.showCreateCaseModal && this.showCreateCaseModal(identity.id)}
      </div>
    )
  }
}

const getIdentity = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      defaultAddressType
      fullName
      identityValue
      isClient
      isContact
      isEmployee
      isEmployer
      summary
      defaultCellphoneNumber: addresses(addressType: "msisdn", default: true, inactive: false) {
        edges {
          node {
            id
            addressValue
          }
        }
      }
      defaultEmailAddress: addresses(addressType: "email", default: true, inactive: false) {
        edges {
          node {
            id
            addressValue
          }
        }
      }
      client {
        id
        summary
        division {
          id
          employer {
            id
          }
        }
      }
      employer {
        id
        friendlyName
        employergroup {
          id
          friendlyName
        }
      }
    }
  }
`

const withData =
  graphql(getIdentity, {
    options: (ownProps) => ({
      variables: {
        id: ownProps.match.params.identityId
      }
    })
  })

export default compose(
  withData,
  withRouter
)(Identity)



// WEBPACK FOOTER //
// ./src/containers/Identity.js